import React, { useState, useEffect, useRef, useContext, useCallback, useMemo, memo, lazy, Suspense } from 'react';
import MessageList from './components/MessageList';
import NotificationPanel from './components/NotificationPanel';
import NotePreview from './components/NotePreview';
import InputArea, { ModeBadge } from './components/InputArea';
import SessionIndicators from './components/SessionIndicators';
import SelectionModeOverlay from './components/SelectionModeOverlay';
import GuidedModeSidebar from './components/GuidedModeSidebar';
import { useBriefing } from '@/contexts/BriefingContext';
import { PatientContextPanel } from './components/context-panel';
import DialogModals from './components/DialogModals';
import { NotificationContext } from '@/contexts/NotificationContext';
import { useNoteMode } from '@/contexts/NoteModeContext';
import { useConversation } from '@/contexts/ConversationContext';
import { useDarkMode } from '@/contexts/DarkModeContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useVoiceMode } from './hooks/useVoiceMode';
import { useGuidedWorkflow } from './hooks/useGuidedWorkflow';
import { useMessageLoading } from './hooks/useMessageLoading';
import { useScrollManagement } from './hooks/useScrollManagement';
import { useMessageActions } from './hooks/useMessageActions';
import { useNoteCompletionEvents } from './hooks/useNoteCompletionEvents';
import { useSessionManagement } from './hooks/useSessionManagement';
import { AIWorkflowTooltips } from '@/components/onboarding';
import { useAIHealthStatus } from '@/hooks/useAIHealthStatus';
import { getMobileMaxWidth } from '../../utils/mobileStyles';
import { useGuidedWorkflowDisposition } from './hooks/useGuidedWorkflowDisposition';
import { useAIGenerationJobRecovery } from '@/hooks/useAIGenerationJobRecovery';
import AIPreferencesPill from '@/features/my-settings/components/AIPreferencesPill';

const InlineCanvasPanel = lazy(() => import('./components/InlineCanvasPanel'));
const ProjectTemplateSuggestions = lazy(() => import('./components/ProjectTemplateSuggestions'));

function TherapistPanel({ user }) {
  const [input, setInput] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showSaveIdeaDialog, setShowSaveIdeaDialog] = useState(false);
  const [ideaToSave, setIdeaToSave] = useState("");
  const [messageToSave, setMessageToSave] = useState(null);
  const [selectedText, setSelectedText] = useState("");
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [viewingNoteInline, setViewingNoteInline] = useState(false);
  const [inlineNoteData, setInlineNoteData] = useState(null);
  const [batchNotes, setBatchNotes] = useState(null);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [currentEncounter, setCurrentEncounter] = useState(null);
  const [modeOverride, setModeOverride] = useState<string | null>(null);
  const [canvasOpen, setCanvasOpen] = useState(false);

  const { voiceModeActive, setVoiceModeActive } = useVoiceMode();
  const { 
    guidedMode, 
    guidedWorkflowData,
    saveProgress,
    isSavingProgress,
    fetchGuidedWorkflowData,
  } = useGuidedWorkflow();
  
  const { isDarkMode } = useDarkMode();
  const isMobile = useIsMobile();
  const { aiAvailable } = useAIHealthStatus();
  const { briefingData } = useBriefing();

  const notificationContext = useContext(NotificationContext);
  const addNoteCreatedNotification = notificationContext?.addNoteCreatedNotification || (() => {});
  const soapProgress = notificationContext?.soapProgress;
  const noteModeContext = useNoteMode();
  const { noteMode, selectedNoteTypes } = noteModeContext || { noteMode: false, selectedNoteTypes: { soap: true, client_summary: false, therapist_reflection: false }};
  const {
    currentSessionId,
    startNewConversation,
    updateScrollPosition,
    fetchConversationMessages,
    conversations,
    hasFetched,
    isProcessing,
    setIsProcessing,
    cancelProcessing,
    error,
    setError,
    sendMessage: contextSendMessage,
    rehydrating,
    isLoading: contextLoading,
    messages: contextMessages,
    cacheDisplayMessages,
    getCachedDisplayMessages,
    getGuidedSessionInfo,
    setGuidedSessionInfo,
    clearGuidedSessionInfo,
    getGuidedPromptSentForSession,
    setGuidedPromptSentForSession,
    clearGuidedPromptSentForSession,
    aiCurrentPatient,
    setAiCurrentPatient
  } = useConversation();
  
  const inputRef = useRef(null);

  const {
    messages,
    setMessages,
    isLoadingMessages,
    currentPage,
    totalPages,
    hasMoreMessages,
    setCurrentPage,
    setHasMoreMessages,
    loadConversationMessages
  } = useMessageLoading({
    currentSessionId,
    user,
    fetchConversationMessages,
    contextMessages,
    hasFetched,
    cacheDisplayMessages,
    getCachedDisplayMessages
  });

  // Derive current AI mode from the latest assistant message
  const aiMode = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.sender === 'assistant' && messages[i]?.ai_mode) {
        return messages[i].ai_mode;
      }
    }
    return 'conversation';
  }, [messages]);

  const {
    messagesEndRef,
    mainContentRef,
    nearBottomRef
  } = useScrollManagement({
    messages,
    currentSessionId
  });

  const {
    sendMessage,
    retryMessage,
    fetchNoteData,
    renderMessageWithViewNoteLinks,
    successMessages,
    setSuccessMessages,
    noteError,
    currentRequestId,
    clearCurrentRequestId,
    setCurrentRequestId
  } = useMessageActions({
    input,
    setInput,
    setMessages,
    noteMode,
    selectedNoteTypes,
    contextSendMessage,
    currentSessionId,
    currentPatient,
    currentEncounter,
    setError,
    setCurrentPage,
    setHasMoreMessages,
    addNoteCreatedNotification,
    setViewingNoteInline,
    setInlineNoteData,
    messagesEndRef
  });

  const clearNoteWorkflowState = useCallback(() => {
    setViewingNoteInline(false);
    setInlineNoteData(null);
    setBatchNotes(null);
    setCurrentNoteIndex(0);
    clearCurrentRequestId();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [clearCurrentRequestId, inputRef, setBatchNotes, setCurrentNoteIndex, setInlineNoteData, setViewingNoteInline]);

  const appendAssistantMessage = useCallback((text: string) => {
    setMessages((prev) => [...prev, { sender: 'assistant', text }]);
  }, [setMessages]);

  const handleDisposition = useGuidedWorkflowDisposition({
    guidedMode,
    currentSessionId,
    clearNoteWorkflowState,
    setCurrentPatient,
    setCurrentEncounter,
    setAiCurrentPatient,
    appendAssistantMessage,
    fetchGuidedWorkflowData,
  });

  useSessionManagement({
    user,
    currentSessionId,
    startNewConversation,
    rehydrating,
    contextLoading,
    conversations,
    hasFetched,
    loadConversationMessages,
    setMessages,
    setCurrentPatient,
    setCurrentEncounter,
    guidedMode,
    guidedWorkflowData,
    sendMessage,
    getGuidedSessionInfo,
    setGuidedSessionInfo,
    clearGuidedSessionInfo,
    getGuidedPromptSentForSession,
    setGuidedPromptSentForSession,
    clearGuidedPromptSentForSession
  });

  useNoteCompletionEvents({
    currentSessionId,
    currentRequestId,
    clearCurrentRequestId,
    setIsProcessing,
    setMessages,
    setBatchNotes,
    setCurrentNoteIndex,
    guidedMode,
    guidedWorkflowData,
    messagesEndRef,
  });
  useAIGenerationJobRecovery({ currentRequestId, sessionId: currentSessionId, setCurrentRequestId, setIsProcessing, storageNamespace: 'assistant' });

  useEffect(() => {
    const handleFocusChange = () => {
      const activeElement = document.activeElement;
      const isFocused = activeElement === inputRef.current;
      setIsInputFocused(isFocused);
    };

    document.addEventListener('focusin', handleFocusChange);
    document.addEventListener('focusout', handleFocusChange);

    return () => {
      document.removeEventListener('focusin', handleFocusChange);
      document.removeEventListener('focusout', handleFocusChange);
    };
  }, []);

  const captureSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim()) {
      setSelectedText(selection.toString().trim());
      return selection.toString().trim();
    }
    return "";
  }, []);

  return (
    <div data-testid="therapist-panel-root" className="flex flex-col h-full min-h-0 bg-background dark:bg-gray-900 font-sans">
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <main
          className="flex-1 flex flex-col p-2 sm:p-3 md:p-4 relative bg-background dark:bg-gray-900 min-h-0 transition-all duration-300 ease-in-out overflow-hidden"
          style={{ paddingTop: '0.5rem' }}
        >
                    <div className="flex items-center gap-2 flex-wrap">
                      <SessionIndicators />
                    </div>
                    <AIWorkflowTooltips user={user} variant="banner" />

                    {!aiAvailable && (
                      <div
                        role="alert"
                        className="flex items-center gap-2 px-4 py-2.5 mb-2 rounded-lg text-sm font-medium bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200"
                      >
                        <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        AI assistant is temporarily unavailable. You can still view and edit notes manually. This banner will dismiss automatically when service is restored.
                      </div>
                    )}
          
                    {isSelectionMode && (
            <SelectionModeOverlay
              selectedMessages={selectedMessages}
              onCancel={() => {
                setIsSelectionMode(false);
                setSelectedMessages([]);
              }}
              onSave={() => {
                if (selectedMessages.length > 0) {
                  setShowSaveIdeaDialog(true);
                }
              }}
            />
          )}

          <div className="flex flex-row gap-4 w-full flex-1 min-h-0 overflow-hidden">
            {/* Chat area — main content on the left (mirrors calendar position on Dashboard) */}
            <div className={`flex flex-col flex-1 min-w-0 ${getMobileMaxWidth(isMobile)} bg-white dark:bg-gray-800 border border-light-border dark:border-gray-700 shadow-apple rounded-2xl overflow-visible`}>
                <div className="sticky top-0 z-50 shrink-0 flex items-center gap-2 px-3 pt-2 bg-white dark:bg-gray-800">
                  <AIPreferencesPill />
                  <ModeBadge
                    aiMode={guidedMode ? 'note_writing' : aiMode}
                    modeOverride={modeOverride}
                    onModeOverride={setModeOverride}
                  />
                </div>
                <div 
                  ref={mainContentRef}
                  className={`${viewingNoteInline ? 'max-h-[50vh] sm:max-h-[30vh]' : 'flex-1'} relative overflow-y-auto space-y-3 sm:space-y-4 scrollbar-thin scrollbar-thumb-rounded px-3 py-3 sm:px-4 sm:py-4`}
                  style={{
                    minHeight: 0,
                    paddingBottom: '16px',
                    overflowY: 'auto',
                    scrollBehavior: 'smooth',
                    WebkitOverflowScrolling: 'touch'
                  }}
                  onScroll={() => {
                    if (mainContentRef.current) {
                      updateScrollPosition(mainContentRef.current.scrollTop);
                    }
                  }}
                >
                  {hasMoreMessages && (
                    <div className="flex justify-center pb-2">
                      <button
                        className="text-sm px-4 py-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors active:bg-gray-400 dark:active:bg-gray-500"
                        onClick={() => loadConversationMessages(currentSessionId, currentPage + 1, false)}
                        disabled={isLoadingMessages}
                      >
                        {isLoadingMessages ? "Loading..." : "Load More"}
                      </button>
                    </div>
                  )}
                  <div className="w-full">
                    <div className="max-w-[960px] mx-auto px-4">
                      <MessageList
                        messages={messages}
                        isSelectionMode={isSelectionMode}
                        selectedMessages={selectedMessages}
                        setSelectedMessages={setSelectedMessages}
                        captureSelection={captureSelection}
                        renderMessageWithViewNoteLinks={renderMessageWithViewNoteLinks}
                        sendMessage={sendMessage}
                        retryMessage={retryMessage}
                        messagesEndRef={messagesEndRef}
                        isProcessing={isProcessing}
                        onCancelProcessing={cancelProcessing}
                        currentPatient={currentPatient}
                        currentEncounter={currentEncounter}
                        setCurrentPatient={setCurrentPatient}
                        setCurrentEncounter={setCurrentEncounter}
                        fetchNoteData={fetchNoteData}
                        guidedMode={guidedMode}
                      />
                    </div>
                  </div>
                </div>

                <NotificationPanel
                  successMessages={successMessages}
                  setSuccessMessages={setSuccessMessages}
                  noteError={noteError}
                  error={error}
                  setError={setError}
                  fetchNoteData={fetchNoteData}
                  soapProgress={soapProgress}
                />

                <NotePreview
                  viewingNoteInline={viewingNoteInline}
                  setViewingNoteInline={(v) => {
                    setViewingNoteInline(v);
                    if (!v && inputRef.current) inputRef.current.focus();
                  }}
                  inlineNoteData={inlineNoteData}
                  setInlineNoteData={setInlineNoteData}
                  batchNotes={batchNotes}
                  setBatchNotes={setBatchNotes}
                  currentNoteIndex={currentNoteIndex}
                  setCurrentNoteIndex={setCurrentNoteIndex}
                  fetchNoteData={fetchNoteData}
                  onDisposition={handleDisposition}
                  onWriteNextNote={() => {
                    clearCurrentRequestId();
                    setCurrentPatient(null);
                    setCurrentEncounter(null);
                    setBatchNotes(null);
                    setCurrentNoteIndex(0);
                    setInlineNoteData(null);
                    if (inputRef.current) {
                      inputRef.current.focus();
                    }
                  }}
                />

                {/* Smart template suggestions when writing intent detected */}
                {!canvasOpen && !guidedMode && (
                  <Suspense fallback={null}>
                    <ProjectTemplateSuggestions
                      messages={messages}
                      sessionId={currentSessionId}
                      onOpenCanvas={isMobile ? undefined : () => setCanvasOpen(true)}
                    />
                  </Suspense>
                )}

                <InputArea
                  input={input}
                  setInput={setInput}
                  sendMessage={sendMessage}
                  isProcessing={isProcessing}
                  inputRef={inputRef}
                  voiceModeActive={voiceModeActive}
                  setVoiceModeActive={setVoiceModeActive}
                  onOpenCanvas={isMobile ? undefined : () => setCanvasOpen(true)}
                  showCanvasAction={!guidedMode}
                  showCapabilityInfo={!guidedMode}
                />

            </div>

            {/* Right sidebar — Catch Up sessions for both guided entry points */}
            {guidedMode && guidedWorkflowData && !isMobile && (
              <div className="w-[30%] shrink-0 bg-white dark:bg-gray-800 border border-light-border dark:border-gray-700 shadow-sm rounded-lg overflow-y-auto min-h-0 pb-4">
                <GuidedModeSidebar
                  guidedWorkflowData={guidedWorkflowData}
                  dashboardData={briefingData}
                  currentEncounter={currentEncounter}
                  onSaveProgress={saveProgress}
                  isSavingProgress={isSavingProgress}
                  onNoteClick={async (encounterId) => {
                    const allSessions = (guidedWorkflowData.notes || []).flatMap(p => p.sessions.map(s => ({...s, patient_name: p.patient_name, patient_id: p.patient_id})));
                    const selectedSession = allSessions.find(s => s.encounter_id === encounterId);
                    if (selectedSession) {
                      setCurrentEncounter({ id: encounterId, note_id: selectedSession.note_id });
                      setCurrentPatient({ id: selectedSession.patient_id, name: selectedSession.patient_name });
                      if (selectedSession.note_id) {
                        await fetchNoteData(selectedSession.note_id);
                      } else {
                        appendAssistantMessage(`Tell me what happened during ${selectedSession.patient_name}'s session on ${selectedSession.date}, and I'll help you document it.`);
                      }
                    }
                  }}
                />
              </div>
            )}
            {/* Patient context panel — shows when no guided-mode sidebar and no canvas active */}
            {!guidedMode && !canvasOpen && !isMobile && (
              <div className="w-[30%] shrink-0 bg-white dark:bg-gray-800 border border-light-border dark:border-gray-700 shadow-sm rounded-lg overflow-y-auto min-h-0 pb-4">
                <PatientContextPanel
                  currentPatient={aiCurrentPatient}
                  sessionId={currentSessionId}
                  onPatientChange={setAiCurrentPatient}
                />
              </div>
            )}

            {/* Inline Canvas Panel — document editor side panel */}
            {canvasOpen && !isMobile && (
              <div className="w-[40%] shrink-0 rounded-lg overflow-hidden shadow-sm min-h-0">
                <Suspense fallback={<div className="h-full flex items-center justify-center bg-white dark:bg-gray-800"><span className="text-sm text-gray-500">Loading Canvas...</span></div>}>
                  <InlineCanvasPanel
                    onClose={() => setCanvasOpen(false)}
                    messages={messages}
                  />
                </Suspense>
              </div>
            )}
          </div>

          <DialogModals
            showSaveIdeaDialog={showSaveIdeaDialog}
            setShowSaveIdeaDialog={setShowSaveIdeaDialog}
            messageToSave={messageToSave}
            messages={messages}
            selectedMessages={selectedMessages}
            setSelectedMessages={setSelectedMessages}
            isSelectionMode={isSelectionMode}
            setIsSelectionMode={setIsSelectionMode}
          />
        </main>
      </div>
    </div>
  );
}

export default memo(TherapistPanel);
